import { useEffect, useRef, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import styles from './GlobalLoader.module.css';

// Loader transversal: refleja CUALQUIER request en vuelo de la app sin tocar
// cada ruta (se apoya en el estado global de TanStack Query). Dos capas:
//  1) barra de progreso superior — aparece de inmediato, no bloquea.
//  2) overlay tenue con blur — solo si la carga supera OVERLAY_DELAY, para
//     que las cargas rápidas/cacheadas no generen un flash molesto.

const OVERLAY_DELAY = 350; // ms antes de atenuar la pantalla

export function GlobalLoader() {
  // Solo cargas iniciales (query aún sin data) disparan el loader. Los refetch
  // de fondo — polling de la campana de notificaciones, refetchOnWindowFocus —
  // NO deben mostrar chrome de "cargando": el usuario no inició esa actividad y
  // el parpadeo cada X segundos es molesto al editar (p. ej. en /cms).
  const fetching = useIsFetching({ predicate: (q) => q.state.data === undefined });
  const mutating = useIsMutating();
  const busy = fetching + mutating > 0;

  const [showOverlay, setShowOverlay] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (busy) {
      if (timerRef.current === null) {
        timerRef.current = setTimeout(() => setShowOverlay(true), OVERLAY_DELAY);
      }
    } else {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setShowOverlay(false);
    }
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [busy]);

  if (!busy) return null;

  return (
    <>
      <div className={styles.bar} role="presentation" aria-hidden>
        <div className={styles.barIndicator} />
      </div>
      {showOverlay && (
        <div className={styles.overlay} role="status" aria-live="polite" aria-label="Cargando">
          <div className={styles.spinner} aria-hidden />
          <span className={styles.label}>Cargando…</span>
        </div>
      )}
    </>
  );
}
